import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { BarChart3, CheckCircle2, LocateFixed, RefreshCw, Trees } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, number } from '../lib/format'

type Position = { latitude: number; longitude: number; accuracy: number }

export function PlotMonitorLive() {
  const [plots, setPlots] = useState<any[]>([])
  const [plotId, setPlotId] = useState('')
  const [observations, setObservations] = useState<any[]>([])
  const [position, setPosition] = useState<Position | null>(null)
  const [locating, setLocating] = useState(false)
  const [loadingPlots, setLoadingPlots] = useState(true)
  const [loadingObservations, setLoadingObservations] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState<any>(null)
  const [busy, setBusy] = useState(false)

  const loadPlots = async () => {
    setLoadingPlots(true)
    try {
      const rows = await api.plots()
      setPlots(rows.filter((plot) => plot.monitoring_mode === 'plot' || plot.monitoring_mode === 'hybrid'))
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoadingPlots(false)
    }
  }

  useEffect(() => { void loadPlots() }, [])
  useEffect(() => {
    let current = true
    if (!plotId) {
      setObservations([])
      setLoadingObservations(false)
      return () => { current = false }
    }
    setLoadingObservations(true)
    api.plotObservations(plotId).then((rows) => {
      if (current) setObservations(rows)
    }).catch((e) => {
      if (current) setError(e instanceof Error ? e.message : String(e))
    }).finally(() => {
      if (current) setLoadingObservations(false)
    })
    return () => { current = false }
  }, [plotId])

  const selectedPlot = useMemo(() => plots.find((plot) => plot.id === plotId), [plots, plotId])
  const latest = observations[0]

  const locate = () => {
    if (!navigator.geolocation) {
      setError('Location services are unavailable in this browser.')
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPosition({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy })
        setLocating(false)
      },
      (e) => {
        setError(e.message)
        setLocating(false)
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 5_000 },
    )
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!plotId) {
      setError('Choose a monitored plot.')
      return
    }
    if (!position) {
      setError('Capture your field location before submitting the sample.')
      return
    }

    const form = new FormData(event.currentTarget)
    const payload = {
      plotId,
      observedAt: new Date().toISOString(),
      sampleSize: Number(form.get('sampleSize')),
      aliveCount: Number(form.get('aliveCount')),
      deadCount: Number(form.get('deadCount')),
      stressedCount: Number(form.get('stressedCount')),
      unknownCount: Number(form.get('unknownCount')),
      estimatedTotal: selectedPlot?.target_count ? Number(selectedPlot.target_count) : undefined,
      notes: String(form.get('notes') || '').trim() || null,
      location: position,
    }
    const counted = payload.aliveCount + payload.deadCount + payload.stressedCount + payload.unknownCount
    if (counted !== payload.sampleSize) {
      setError(`The condition counts total ${counted}, but sample size is ${payload.sampleSize}. Every sampled sapling must be classified.`)
      return
    }

    setBusy(true)
    setError('')
    setSuccess(null)
    try {
      const result = await api.createPlotObservation(payload)
      setSuccess(result)
      setPosition(null)
      event.currentTarget.reset()
      setObservations(await api.plotObservations(plotId))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const latestSurvival = latest?.sample_size ? Math.round(Number(latest.alive_count || 0) / Number(latest.sample_size) * 1000) / 10 : null

  return (
    <div className="page-stack">
      <section className="page-header-row">
        <div><h2>Plot monitoring</h2><p>Record representative survival samples for plot and hybrid monitoring units.</p></div>
        <button className="button button--secondary" onClick={() => void loadPlots()} disabled={loadingPlots}><RefreshCw size={17} /> Refresh</button>
      </section>

      {error && <section className="panel form-feedback" role="alert"><strong>Sample needs attention</strong><p>{error}</p></section>}
      {success && <section className="panel form-feedback"><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><CheckCircle2 size={28} /><div><strong>Plot sample recorded</strong><p>{success.sampleSize} saplings assessed · {success.survivalPercent}% observed alive in this sample.</p></div></div></section>}

      <div className="dashboard-grid">
        <form className="panel" onSubmit={submit} aria-busy={loadingPlots}>
          <div className="panel-heading"><div><span className="eyebrow">FIELD SAMPLE</span><h3>Representative survival check</h3></div><Trees size={20} /></div>
          {loadingPlots && <p role="status" style={{ padding: '0 19px', margin: 0 }}>Loading monitored plots…</p>}
          <label className="field-label"><span>Monitoring plot</span><select required value={plotId} onChange={(event) => { setPlotId(event.target.value); setSuccess(null) }} disabled={loadingPlots || busy}><option value="">Choose plot</option>{plots.map((plot) => <option key={plot.id} value={plot.id}>{plot.name} · {plot.site_name} · {plot.monitoring_mode}</option>)}</select></label>
          {!loadingPlots && plots.length === 0 && <p className="empty-stat">No plot or hybrid monitoring units are configured.</p>}
          {selectedPlot && <div className="record-list"><div><span>Monitoring mode</span><strong>{selectedPlot.monitoring_mode}</strong></div><div><span>Target population</span><strong>{number.format(Number(selectedPlot.target_count || 0))}</strong></div><div><span>Plot code</span><strong>{selectedPlot.code}</strong></div></div>}

          <div className="dashboard-grid">
            <label className="field-label"><span>Sample size</span><input required name="sampleSize" type="number" min="1" max="100000" disabled={loadingPlots || busy} /></label>
            <label className="field-label"><span>Alive</span><input required name="aliveCount" type="number" min="0" defaultValue="0" disabled={loadingPlots || busy} /></label>
          </div>
          <div className="dashboard-grid">
            <label className="field-label"><span>Dead</span><input required name="deadCount" type="number" min="0" defaultValue="0" disabled={loadingPlots || busy} /></label>
            <label className="field-label"><span>Stressed</span><input required name="stressedCount" type="number" min="0" defaultValue="0" disabled={loadingPlots || busy} /></label>
          </div>
          <label className="field-label"><span>Unknown / uncertain</span><input required name="unknownCount" type="number" min="0" defaultValue="0" disabled={loadingPlots || busy} /></label>
          <label className="field-label"><span>Field notes <em>optional</em></span><textarea name="notes" rows={4} placeholder="Sampling route, grazing pressure, irrigation, mortality pattern, weather, etc." disabled={loadingPlots || busy} /></label>
          <button type="button" className="button button--secondary button--wide" onClick={locate} disabled={locating || loadingPlots || busy}><LocateFixed size={16} />{locating ? ' Locating…' : position ? ` GPS captured ±${Math.round(position.accuracy)}m` : ' Capture field GPS'}</button>
          {position && position.accuracy > 75 && <p className="passport-smallprint">Location accuracy is ±{Math.round(position.accuracy)} m. Plot monitoring tolerates more uncertainty than individual-tree activation, but open sky is preferable.</p>}
          <button className="button button--primary button--wide" disabled={busy || loadingPlots || !plotId} style={{ marginTop: 12 }}><BarChart3 size={17} />{busy ? ' Recording…' : ' Record survival sample'}</button>
        </form>

        <section className="panel">
          <div className="panel-heading"><div><h3>{selectedPlot?.name || 'Choose a plot'}</h3></div><BarChart3 size={20} /></div>
          {latest && <div className="metric-grid" style={{ gridTemplateColumns: 'repeat(2,minmax(0,1fr))' }}><div className="metric-card"><span>Latest survival</span><strong>{latestSurvival}%</strong><small>{latest.alive_count}/{latest.sample_size} observed alive</small></div><div className="metric-card"><span>Last sample</span><strong>{latest.sample_size}</strong><small>{formatDateTime(latest.observed_at)}</small></div></div>}
          {loadingObservations ? <div className="empty-state" role="status"><span>Loading sample history…</span></div> : <div className="campaign-list">{observations.map((row) => {
            const survival = Number(row.sample_size) ? Math.round(Number(row.alive_count) / Number(row.sample_size) * 1000) / 10 : 0
            return <article className="campaign-row" key={row.id}><div><strong>{survival}% alive · sample {row.sample_size}</strong><span>{formatDateTime(row.observed_at)} · {row.dead_count} dead · {row.stressed_count} stressed · {row.unknown_count} unknown</span>{row.notes && <p>{row.notes}</p>}</div></article>
          })}{plotId && observations.length === 0 && <div className="empty-state"><strong>No samples recorded</strong><span>The first sample for this plot will appear here.</span></div>}{!plotId && <div className="empty-state"><span>Select a plot to view its monitoring history.</span></div>}</div>}
        </section>
      </div>
    </div>
  )
}

